import Grid from "../shared/Grid/Grid";
import { GridColumnDefs } from "../../models/Grid/gridColumnDefs";
import { getPatientBillingReportInfo } from "../../models/request/getPatientBillingReportInfoRequest";
import { useEffect, useMemo, useState } from "react";
import { userManagement_LABELS } from "../../constants/labels/UserManagement";
import MultiSelectDropdown from "../shared/MultiSelectDropdown/MultiSelectDropdown";
import { Button } from "@mui/material";
import { ReactComponent as ExportIcon } from "../../assets/svg/Export.svg";
import DaterangePicker from "../shared/DaterangePicker/DaterangePicker";
import { Utility } from "../../utils/Utility";
import Download from "../shared/SVG/Download";
import * as dateFns from "date-fns";
import "./ReportingGrid.scss";
import "../shared/ConfirmationDialog/ConfirmationDialog.scss";
import ConfirmationDialog from "../shared/ConfirmationDialog/ConfirmationDialog";
import { ReactComponent as DoneIcon } from "../../assets/svg/Done.svg";
import { ReactComponent as InfoIcon } from "../../assets/svg/info_lg.svg";
import { reportPage_LABELS } from "../../constants/labels/ReportingPage";
import {
  useLazyGetPatientBillingReportInfoQuery,
  useLazyDownloadReportQuery,
  useLazyGenerateReportQuery,
} from "../../services/rtkQueryServices/reportingService";

function ReportingGrid(props: any) {
  const [rowData, setRowData] = useState<any[]>([]);
  const [rowcount, setRowCount] = useState<number>(0);
  const [showExportPopup, setShowExportPopup] = useState<boolean>(false);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [showDownloadErrorPopup, setShowDownloadErrorPopup] =
    useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [error, setError] = useState<string>("");
  const applicationUserId = props?.userId;
  const [filterCriteria, setFilterCriteria] =
    useState<getPatientBillingReportInfo>({
      applicationUserId: props?.userId,
      pageNumber: 1,
      pageSize: 10,
      sortByColumnName: "createdDate",
      sortOrder: "desc",
      filters: [],
      timezoneOffSet: 0,
      dateRangeCondition: dateRange?.length
        ? {
          fromDate: dateRange[0]?.toLocaleDateString("fr-CA"),
          toDate: dateRange[1]?.toLocaleDateString("fr-CA"),
        }
        : null,
    });
  const [isApplyFiltersEnabled, setIsApplyFiltersEnabled] =
    useState<boolean>(false);
  const [resetPagination, setResetPagination] = useState<boolean>(false);
  const [getPatientBillingReportInfo] =
    useLazyGetPatientBillingReportInfoQuery();
  const [downloadExcel] = useLazyDownloadReportQuery();
  const [generateReport] = useLazyGenerateReportQuery();

  const getPatientBillingInformation = () => {
    getPatientBillingReportInfo(filterCriteria).then((res: any) => {
      if (res.isSuccess && res.data?.data) {
        setRowData(res?.data?.data?.listOfPatientBillingReports);
        setRowCount(res?.data?.data?.totalCount);
      }
    });
  };

  const isBpSiteIdVerified = useMemo(() => {
    return props?.isBpSiteIdVerified;
  }, [props?.isBpSiteIdVerified]);
  const isSubscriptionActive = useMemo(() => {
    return props?.isSubscriptionActive;
  }, [props?.isSubscriptionActive]);

  useEffect(() => {
    if (isSubscriptionActive === true && isBpSiteIdVerified === true) {
      getPatientBillingInformation();
    }
  }, [filterCriteria, isSubscriptionActive, isBpSiteIdVerified]);

  const ReportRequestId = (props: any) => {
    return (
      <div title={props?.data?.requestId}>
        {props?.data?.requestId.slice(0, 5)}
      </div>
    );
  };

  const fromToDate = (props: any) => {
    var fromDate = Utility.ConvertToDate(props?.data?.fromDate);
    var toDate = Utility.ConvertToDate(props?.data?.toDate);
    return `${fromDate} - ${toDate}`;
  };

  const RequestedDate = (props: any) => {
    const onlyDate = Utility.ConvertToDate(props?.data?.requestedDate);
    return onlyDate;
  };

  const RequestedBy = (props: any) => {
    if (props?.data?.hasRequestedBySuperUser) {
      return `${props?.data?.requestedBy} (Inca Support)`;
    } else {
      return `${props?.data?.requestedBy}`;
    }
  };

  const Actions = (props: any) => {
    var downloadExcelRequest: any = {
      applicationUserId: applicationUserId,
      requestId: props?.data?.requestId,
    };

    const getDownloadText = (status: string) => {
      switch (status) {
        case reportPage_LABELS.Intiated:
          return "Request is processing.";
        case reportPage_LABELS.InProgress:
          return "File generation is in progress.";
        case reportPage_LABELS.Generated:
          return "Download File now";
        case reportPage_LABELS.Failed:
          return "File generation failed. Try again.";
        case reportPage_LABELS.Expired:
          return "File not available to download.";
        case reportPage_LABELS.NoData:
          return "Records not available in the selected date range";
        default:
          return "";
      }
    };

    const handleDownload = () => {
      downloadExcel(downloadExcelRequest).then((res: any) => {
        if (res?.isSuccess && res?.data?.data) {
          const blob = new Blob([res?.data?.data], {
            type:
              res?.data?.headers?.get("Content-Type") ||
              "application/octet-stream",
          });

          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            Utility.getFileNameFromResponseHeaders(res?.data?.headers) ||
            "Report.xlsx";
          link.click();
          setTimeout(() => {
            URL.revokeObjectURL(link.href);
          }, 100);
        } else if (res?.isError) {
          setShowDownloadErrorPopup(true);
        }
      });
    };

    return (
      <div title={getDownloadText(props?.data.documentStatus)}>
        {props?.data?.documentStatus === reportPage_LABELS.Generated ||
          props?.data?.documentStatus === reportPage_LABELS.Expired ? (
          <div>
            <Button
              className="download"
              onClick={handleDownload}
              disabled={
                props?.data?.documentStatus === reportPage_LABELS.Expired
              }
            >
              <Download />
              Download
            </Button>
          </div>
        ) : (
          <div>
            {props?.data?.documentStatus === reportPage_LABELS.NoData
              ? "No Data Found"
              : props?.data?.documentStatus === reportPage_LABELS.InProgress
                ? "In Progress"
                : props?.data?.documentStatus}
          </div>
        )}
      </div>
    );
  };

  const columnDefs: GridColumnDefs[] = [
    {
      headerName: userManagement_LABELS.requestId,
      field: "requestId",
      cellRenderer: ReportRequestId,
    },
    {
      headerName: userManagement_LABELS.fromToDate,
      field: "fromDate",
      cellRenderer: fromToDate,
    },
    {
      headerName: userManagement_LABELS.requestedBy,
      field: "requestedBy",
      cellRenderer: RequestedBy,
    },
    {
      headerName: userManagement_LABELS.requestedDate,
      field: "requestedDate",
      cellRenderer: RequestedDate,
    },
    {
      headerName: userManagement_LABELS.actionStatus,
      field: "actions",
      cellRenderer: Actions,
      align: "center",
    },
  ];
  const onPageChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: value,
    });
  };
  const onPageSizeChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: 1,
      pageSize: value,
    });
  };

  return (
    <>
      <div className="clinic-management-wrapper clinic-grid-wrapper overflow-scroll d-flex flex-column">
        <Grid
          columnDefs={columnDefs}
          uniqueIdentifierFieldName="id"
          resetPagination={resetPagination}
          rowData={rowData}
          serverSidePagination
          rowCount={rowcount}
          serverSideSorting
          showPagination
          showFilterIcon={false}
          pageSizeList={[10, 20, 50]}
          pageSize={10}
          masterDetail
          showTotalRowCount
          isReporting={true}
          isHeaderDisabled={
            (isSubscriptionActive !== null && !isSubscriptionActive) ||
            (isBpSiteIdVerified !== null && !isBpSiteIdVerified)
          }
          filterTemplate={[
            <>
              <div className="d-flex align-items-center gap-2">
                <div className="date-range-label">
                  Select Date Range
                </div>
                <DaterangePicker
                  dateRange={dateRange}
                  onChange={(val: any) => {
                    if (val !== null) {
                      var minStartDate = new Date(
                        dateFns
                          .startOfDay(dateFns.subMonths(new Date(), 13))
                          .setDate(1)
                      );
                      var maxEndDate = dateFns.endOfDay(new Date());
                      var startDate = new Date(val[0]);
                      var endDate = new Date(val[1]);
                      if (startDate < minStartDate || endDate > maxEndDate) {
                        setError("Select date range within past 13 months");
                        setIsApplyFiltersEnabled(false);
                      } else {
                        setError("");
                        setIsApplyFiltersEnabled(true);
                      }
                    } else {
                      setError("");
                      setIsApplyFiltersEnabled(false);
                    }
                    setDateRange(val);
                  }}
                  allowedDateRange={{
                    fromDate: dateFns
                      .startOfDay(dateFns.subMonths(new Date(), 13))
                      .setDate(1),
                    toDate: new Date().toLocaleDateString("fr-CA"),
                  }}
                />
                {error && (
                  <div style={{ paddingLeft: "5px" }} className="error-message">
                    {error}
                  </div>
                )}
              </div>
              <div className="pt-2 px-2">
                <Button
                  className="export"
                  disabled={!isApplyFiltersEnabled}
                  onClick={() => {
                    setShowExportPopup(true);
                    setIsApplyFiltersEnabled(false);
                    setResetPagination(true);
                    setDateRange(null);
                    generateReport({
                      applicationUserId: applicationUserId,
                      fromDate: dateRange[0]?.toLocaleDateString("fr-CA"),
                      toDate: dateRange[1]?.toLocaleDateString("fr-CA"),
                      timezoneOffSet: dateRange?.length
                        ? -new Date().getTimezoneOffset()
                        : 0,
                    }).then((res) => {
                      if (res.isError || !res.data?.data) {
                        setShowExportPopup(false);
                        setShowErrorPopup(true);
                      }
                    });
                  }}
                >
                  <ExportIcon />
                  <span>Export</span>
                </Button>
              </div>
            </>,
          ]}
          resetSuccess={() => {
            setResetPagination(false);
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
        {((isSubscriptionActive !== null && !isSubscriptionActive) ||
          (isBpSiteIdVerified !== null && !isBpSiteIdVerified)) && (
            <div className="grid-overlay">
              <div className="overlay-text">
                {isSubscriptionActive !== null && !isSubscriptionActive
                  ? "Renew your subscription to generate reports."
                  : isBpSiteIdVerified !== null &&
                  !isBpSiteIdVerified &&
                  "Verify BP Site ID to generate reports."}
              </div>
            </div>
          )}
      </div>

      <ConfirmationDialog
        open={showExportPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <DoneIcon />
            <div className="confirmation-header">Request Successful</div>
            <p className="pt-4 dialog-body">
              Your request has been received successfully.We will notify you
              over email once the file is ready. Please check your email.
            </p>
          </div>
        }
        onOk={() => {
          setShowExportPopup(false);
          getPatientBillingInformation();
          props?.setTabValue(1);
        }}
        onClose={() => {
          setShowExportPopup(false);
          getPatientBillingInformation();
          props?.setTabValue(1);
        }}
        okButtonName="OK"
        okButtonClassName="success-ok"
      />
      <ConfirmationDialog
        open={showErrorPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <InfoIcon />
            <div className="confirmation-header">Error</div>
            <p className="pt-4 dialog-body">
              We are unable to process your request at this moment, please try
              again later.
            </p>
          </div>
        }
        onOk={() => {
          setShowErrorPopup(false);
        }}
        onClose={() => {
          setShowErrorPopup(false);
        }}
        okButtonName="Okay"
        okButtonClassName="success-ok"
      />
      <ConfirmationDialog
        open={showDownloadErrorPopup}
        contentBody={
          <div className="d-flex flex-column justify-content-center  align-items-center gap-8">
            <InfoIcon />
            <div className="confirmation-header">Error</div>
            <p className="pt-4 dialog-body">File is not available.</p>
          </div>
        }
        onOk={() => {
          setShowDownloadErrorPopup(false);
          getPatientBillingInformation();
        }}
        onClose={() => {
          setShowDownloadErrorPopup(false);
          getPatientBillingInformation();
        }}
        okButtonName="Okay"
        okButtonClassName="success-ok"
      />
    </>
  );
}
export default ReportingGrid;
